import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Board } from '@/entities/board';
import { useWorkspace } from '@/entities/workspace';
import { useShortcutBindings } from '@/entities/viewer';
import { ParticipantsCluster } from '@/features/board-access';
import { BoardDeck } from '@/features/board-deck';
import { BoardDescription } from '@/features/board-description';
import { BoardTitle, FavoriteButton } from '@/features/board-manage';
import { BackLink, type BackTarget } from '@/features/resource-nav';
import { bindingText } from '@/shared/lib/shortcuts';
import { IconButton, Icons, SplitButton } from '@/shared/ui';

import styles from './BoardHeader.module.css';

interface Props {
  board: Board;
  /** May change the board's own settings (name, description, appearance). */
  canEdit: boolean;
  /** May create tasks (the Create task control). */
  canCreate: boolean;
  mobile: boolean;
  /** The board views, shown in this header when the screen is too short for a floating dock. */
  viewSwitcher?: ReactNode;
  workspacePath: string;
  onQuickCreate: () => void;
  onAdvancedCreate: () => void;
  onLabels: () => void;
  onAppearance: () => void;
  onExport: () => void;
  onSettings: () => void;
  onMenu: () => void;
  onNewBoard?: (() => void) | undefined;
  /** Opens the access settings dialog. */
  onAccess: () => void;
}

/**
 * The way back from a board: to the catalog page it was opened from (Home, a workspace, a
 * category list) and, for a direct link, to the workspace that contains it (team workspaces only;
 * the personal workspace has no page worth a link here - its boards live under My boards). A
 * board-only guest is never told which workspace this is: they get a route to the boards they
 * can open instead.
 */
function WorkspaceLink({ board }: { board: Board }) {
  const { t } = useTranslation();
  if (board.workspace_id === null) {
    return (
      <BackLink
        fallback={{ to: '/app/browse/team-boards', label: t('boardHeader.sharedBoards') }}
      />
    );
  }
  return <MemberWorkspaceLink workspaceId={board.workspace_id} />;
}

function MemberWorkspaceLink({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const workspace = useWorkspace(workspaceId);
  let fallback: BackTarget | null = null;
  if (workspace.data) {
    fallback =
      workspace.data.workspace_type !== 'team'
        ? { to: '/app/browse/boards', label: t('nav.myBoards') }
        : {
            to: `/app/workspaces/${workspaceId}`,
            label: workspace.data.name,
            ariaLabel: t('boardHeader.toWorkspace', { name: workspace.data.name }),
          };
  }
  return <BackLink fallback={fallback} />;
}

/**
 * Compact board header: breadcrumb, title (+ favorite) and ONE row of actions - a single primary
 * "create task" (with a menu for the advanced form) and quiet icon buttons - then the description
 * as secondary text. No panel of its own: the board background stays visible. On phones the icon
 * group collapses into the board menu.
 */
export function BoardHeader({
  board,
  canEdit,
  canCreate,
  mobile,
  workspacePath,
  onQuickCreate,
  onAdvancedCreate,
  onLabels,
  onAppearance,
  onExport,
  onSettings,
  onMenu,
  onNewBoard,
  onAccess,
  viewSwitcher,
}: Props) {
  const { t } = useTranslation();
  const bindings = useShortcutBindings();
  const newTaskKeys = bindingText(bindings['new-task']);
  const createTitle = newTaskKeys
    ? `${t('quickCreate.open')} (${newTaskKeys})`
    : t('quickCreate.open');
  return (
    <header className={styles.header} data-mobile={mobile ? 'true' : undefined}>
      <div className={styles.titleBox}>
        <div className={styles.backRow}>
          <WorkspaceLink board={board} />
        </div>
        <BoardTitle board={board} canEdit={canEdit} />
        <span className={styles.favorite}>
          <FavoriteButton board={board} />
        </span>
        <BoardDeck
          workspaceId={board.workspace_id}
          currentBoardId={board.id}
          workspacePath={workspacePath}
          onNewBoard={onNewBoard}
        />
      </div>
      {mobile ? (
        <div className={styles.mobileActions}>
          {viewSwitcher}
          {canCreate && (
            <IconButton
              label={t('quickCreate.open')}
              icon={<Icons.plus />}
              className={styles.mobileCreate}
              onClick={onQuickCreate}
            />
          )}
          <IconButton
            label={t('boards.menu')}
            icon={<Icons.more />}
            aria-haspopup="dialog"
            onClick={onMenu}
          />
        </div>
      ) : (
        <div className={styles.actions} role="group" aria-label={t('boardHeader.actions')}>
          {canCreate && (
            <div className={styles.split}>
              <SplitButton
                label={t('quickCreate.open')}
                icon={<Icons.plus />}
                title={createTitle}
                onMain={onQuickCreate}
                menuLabel={t('boardHeader.createMenu')}
                items={[
                  { label: t('boardHeader.quick'), icon: <Icons.plus />, onSelect: onQuickCreate },
                  {
                    label: t('boardHeader.advanced'),
                    icon: <Icons.edit />,
                    onSelect: onAdvancedCreate,
                  },
                ]}
              />
            </div>
          )}
          <IconButton
            label={t('boardHeader.manageLabels')}
            icon={<Icons.tag />}
            onClick={onLabels}
          />
          {canEdit && (
            <IconButton
              label={t('boards.appearance')}
              icon={<Icons.palette />}
              onClick={onAppearance}
            />
          )}
          <IconButton
            label={t('portability.export.open')}
            icon={<Icons.export />}
            onClick={onExport}
          />
          <IconButton label={t('boards.settings')} icon={<Icons.sliders />} onClick={onSettings} />
          <ParticipantsCluster board={board} onOpenAccess={onAccess} />
        </div>
      )}
      {mobile && (
        // Own row on phones: the title row keeps title, deck, create and menu.
        <div className={styles.mobileCluster}>
          <ParticipantsCluster board={board} onOpenAccess={onAccess} />
        </div>
      )}
      <div className={styles.description}>
        <BoardDescription board={board} canEdit={canEdit} />
      </div>
    </header>
  );
}
